// Entry point: boot the brand and session, then route to the customer app, field app or admin panel.
import { AdminApp } from "./admin/app";
import { CustomerApp } from "./customer/app";
import { FieldApp } from "./field/app";
import { setUnauthorizedHandler } from "./lib/api";
import { brand } from "./lib/brand";
import { initLocale } from "./lib/i18n";
import { match, navigate, onNavigate, route, startsWith } from "./lib/router";
import { can, homePath, isAdminUser, loadSession, session } from "./lib/session";
import { h, mount, resetLocal } from "./lib/ui";
import { ForgotScreen, LoginScreen, MustChangeScreen, SetPasswordScreen } from "./shared/auth";
import { Dialogs, Toasts } from "./shared/components";

initLocale(brand.current.locales.default, brand.current.locales.enabled);

setUnauthorizedHandler(() => {
  session.set({ me: null, homes: [] });
  if (!startsWith("/login")) navigate(`/login?next=${encodeURIComponent(route.path)}`, { replace: true });
});

// Screen-local state is dropped when leaving a screen, so forms start fresh next time.
let lastPath = route.path;
onNavigate(() => {
  if (route.path !== lastPath) resetLocal("screen:");
  lastPath = route.path;
});

function Splash() {
  return <div style="min-height:100vh;display:grid;place-items:center"><img src={brand.current.markUrl} alt="" width="56" height="56" style="border-radius:14px;opacity:.9" /></div>;
}

function redirect(to: string) {
  queueMicrotask(() => navigate(to, { replace: true }));
  return <Splash />;
}

function View() {
  const path = route.path;
  let token = match("/invite/:token");
  if (token) return <SetPasswordScreen token={token.token} purpose="invite" />;
  token = match("/reset/:token");
  if (token) return <SetPasswordScreen token={token.token} purpose="reset" />;

  if (!session.loaded) return <Splash />;
  if (path === "/forgot") return session.me ? redirect(homePath()) : <ForgotScreen />;
  if (path === "/login") return session.me && !session.me.mustChangePassword ? redirect(homePath()) : session.me ? <MustChangeScreen /> : <LoginScreen />;
  if (!session.me) return redirect(`/login${path !== "/" ? `?next=${encodeURIComponent(path)}` : ""}`);
  if (session.me.mustChangePassword) return <MustChangeScreen />;

  if (startsWith("/admin")) return isAdminUser() ? <AdminApp /> : redirect(homePath());
  if (startsWith("/field")) return can("field.visits") ? <FieldApp /> : redirect(homePath());
  if (!session.homes.length && path === "/") {
    if (isAdminUser()) return redirect("/admin");
    if (can("field.visits")) return redirect("/field");
  }
  return <CustomerApp />;
}

mount(document.getElementById("app")!, () => (
  <div>
    <View />
    <Dialogs />
    <Toasts />
  </div>
));

void loadSession();

if ("serviceWorker" in navigator && location.protocol === "https:") {
  navigator.serviceWorker.register("/sw.js").catch(() => {});
}
